import React, { useState } from 'react';
import { HelpCircle, Plus, Trash2 } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useT } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

import {
  type FoodStatusFields,
  type RackOption,
  SEVERITY_EMOJI,
  deriveFoodSeverity,
  serializeFoodStatus,
  getLinkTimeRange,
  setLinkTimeRange,
  mskHhmmToMs,
  msToMskHhmm,
} from './lineModel';
import { Tooltip } from '@/components/ui/tooltip';

/**
 * The Выкладка food-type-status line form (V2). Status/shelves/avg/duration
 * plus the problem-zones list; the circle is derived from avg/duration
 * (deriveFoodSeverity, mirrors the backend) — no severity picker (Alexey
 * 2026-09-08). Zones are picked from a dropdown of this rack_type's
 * racks in the branch; each zone's Grafana link comes from that rack (not typed), and
 * one shared "problem period" (МСК) sets the from/to of every zone link.
 */
export function FoodStatusForm({
  initial,
  rackTypeOptions,
  racksByType,
  day,
  onApply,
  onCancel,
}: {
  initial: FoodStatusFields;
  rackTypeOptions: string[];
  racksByType: Record<string, RackOption[]>;
  day: string;
  onApply: (fields: FoodStatusFields) => void;
  onCancel: () => void;
}) {
  const { t } = useT();
  const [f, setF] = useState<FoodStatusFields>(initial);

  // Problem period (МСК). One range drives every zone link's from/to.
  const firstRange = f.links.map((l) => (l.url ? getLinkTimeRange(l.url) : null)).find((r) => r?.fromMs);
  const [period, setPeriod] = useState<{ start: string; end: string }>({
    start: firstRange?.fromMs != null ? msToMskHhmm(firstRange.fromMs) : '',
    end: firstRange?.toMs != null ? msToMskHhmm(firstRange.toMs) : '',
  });

  const rackOptions = racksByType[f.rackType] ?? [];
  const typeOptions = rackTypeOptions.includes(f.rackType)
    ? rackTypeOptions
    : [f.rackType, ...rackTypeOptions];

  const set = (patch: Partial<FoodStatusFields>) => setF((prev) => ({ ...prev, ...patch }));

  // Build a zone's link: the rack's Grafana base url (from the dropdown) with the
  // problem period applied. Falls back to any existing url for the same name.
  const urlForZone = (name: string, existing: string | null, p = period): string | null => {
    const base = rackOptions.find((o) => o.name === name)?.url ?? existing ?? null;
    if (!base) return null;
    const fromMs = mskHhmmToMs(day, p.start);
    const toMs = mskHhmmToMs(day, p.end);
    return fromMs != null && toMs != null ? setLinkTimeRange(base, fromMs, toMs) : base;
  };

  const changeZone = (i: number, name: string) =>
    set({ links: f.links.map((l, j) => (j === i ? { name, url: urlForZone(name, null) } : l)) });
  const removeZone = (i: number) => set({ links: f.links.filter((_, j) => j !== i) });
  const addZone = () => {
    const used = new Set(f.links.map((l) => l.name));
    const next = rackOptions.find((o) => !used.has(o.name)) ?? rackOptions[0];
    const name = next?.name ?? f.rackType;
    set({ links: [...f.links, { name, url: urlForZone(name, null) }] });
  };
  const applyPeriod = (start: string, end: string) => {
    const p = { start, end };
    setPeriod(p);
    set({ links: f.links.map((l) => ({ ...l, url: urlForZone(l.name, l.url, p) })) });
  };

  // When rack type changes, keep it in sync and re-resolve links against the new type.
  const changeRackType = (rackType: string) => setF((prev) => ({ ...prev, rackType }));

  const previewRows = serializeFoodStatus(f);
  const derivedEmoji = SEVERITY_EMOJI[deriveFoodSeverity(f.avgShortage, f.durationHours)];

  return (
    <div className="flex flex-col gap-4">
      {/* Rack type — dropdown of the report's rack types */}
      <label className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('reportValidation.form.rackType')}</span>
        <Select value={f.rackType} onChange={(e) => changeRackType(e.target.value)}>
          {typeOptions.map((rt) => (
            <option key={rt} value={rt}>
              {rt}
            </option>
          ))}
        </Select>
      </label>

      {/* Status */}
      <div className="flex flex-col gap-1 text-sm">
        <span className="text-muted-foreground">{t('reportValidation.form.status')}</span>
        <div className="flex gap-2">
          <Button
            type="button"
            variant={f.ok ? 'default' : 'outline'}
            size="sm"
            onClick={() => set({ ok: true })}
          >
            🟢 {t('reportValidation.form.statusOk')}
          </Button>
          <Button
            type="button"
            variant={!f.ok ? 'default' : 'outline'}
            size="sm"
            onClick={() => set({ ok: false })}
          >
            {/* The DERIVED circle — follows avg shortage / duration below. */}
            {derivedEmoji} {t('reportValidation.form.statusProblem')}
          </Button>
        </div>
      </div>

      {!f.ok && (
        <>
          {/* Numbers — the circle above is derived from these (no severity picker). */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <label className="flex flex-col gap-1 text-sm">
              <span className="inline-flex items-center gap-1 text-muted-foreground">
                {t('reportValidation.form.shelfCount')}
                <Tooltip content={t('reportValidation.form.shelfCountHelp')}><span className="inline-flex">
                  <HelpCircle className="size-3.5 opacity-60" />
                </span></Tooltip>
              </span>
              <Input
                type="number"
                min={1}
                value={f.shelfCount}
                onChange={(e) => set({ shelfCount: Math.max(1, Number(e.target.value) || 1) })}
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t('reportValidation.form.avgShortage')}</span>
              <Input
                type="number"
                min={0}
                max={100}
                value={f.avgShortage}
                onChange={(e) =>
                  set({ avgShortage: Math.min(100, Math.max(0, Number(e.target.value) || 0)) })
                }
              />
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="text-muted-foreground">{t('reportValidation.form.duration')}</span>
              <Input
                type="number"
                min={0}
                step={0.1}
                value={f.durationHours}
                onChange={(e) => set({ durationHours: Math.max(0, Number(e.target.value) || 0) })}
              />
            </label>
          </div>

          {/* Problem zones — dropdown per zone + one shared period feeding the links */}
          <div className="flex flex-col gap-2 text-sm">
            <span className="text-muted-foreground">{t('reportValidation.form.links')}</span>

            <div className="flex flex-wrap items-center gap-2">
              <span className="text-xs text-muted-foreground">
                {t('reportValidation.form.period')}
              </span>
              <Input
                type="time"
                value={period.start}
                onChange={(e) => applyPeriod(e.target.value, period.end)}
                className="h-8 w-auto"
              />
              <span className="text-muted-foreground">—</span>
              <Input
                type="time"
                value={period.end}
                onChange={(e) => applyPeriod(period.start, e.target.value)}
                className="h-8 w-auto"
              />
              <span className="text-xs text-muted-foreground/70">
                {t('reportValidation.form.periodHint')}
              </span>
            </div>

            {f.links.length === 0 && (
              <span className="text-xs text-muted-foreground/70">
                {t('reportValidation.form.noLinks')}
              </span>
            )}
            {f.links.map((l, i) => (
              <div key={i} className="flex items-center gap-2">
                {rackOptions.length > 0 ? (
                  <Select
                    value={l.name}
                    onChange={(e) => changeZone(i, e.target.value)}
                    className="flex-1"
                  >
                    {(rackOptions.some((o) => o.name === l.name)
                      ? rackOptions
                      : [{ name: l.name, url: l.url }, ...rackOptions]
                    ).map((o) => (
                      <option key={o.name} value={o.name}>
                        {o.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span className="flex-1 truncate">{l.name}</span>
                )}
                <Tooltip content={l.url ?? t('reportValidation.form.noZoneLink')}><span
                  className={cn('text-xs', l.url ? 'text-primary' : 'text-muted-foreground/60')}
                >
                  {l.url ? t('reportValidation.form.zoneLinked') : t('reportValidation.form.noZoneLink')}
                </span></Tooltip>
                <Button type="button" variant="ghost" size="icon-sm" onClick={() => removeZone(i)}>
                  <Trash2 className="size-3.5" />
                </Button>
              </div>
            ))}
            <div>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addZone}
                disabled={rackOptions.length === 0}
              >
                <Plus className="size-3.5" />
                {t('reportValidation.form.addZone')}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Live preview of the exact generated line */}
      <div className="flex flex-col gap-1">
        <span className="text-xs text-muted-foreground">{t('reportValidation.form.preview')}</span>
        <div className="report-body rounded-md border border-border bg-muted/40 p-2 text-sm">
          {previewRows.map((row, i) =>
            row.startsWith('<blockquote') ? (
              <blockquote
                key={i}
                className="mt-1"
                dangerouslySetInnerHTML={{
                  __html: row.replace(/^<blockquote>/, '').replace(/<\/blockquote>$/, ''),
                }}
              />
            ) : (
              <div key={i} dangerouslySetInnerHTML={{ __html: row }} />
            ),
          )}
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button type="button" variant="ghost" size="sm" onClick={onCancel}>
          {t('reportValidation.form.cancel')}
        </Button>
        <Button type="button" size="sm" onClick={() => onApply(f)}>
          {t('reportValidation.form.apply')}
        </Button>
      </div>
    </div>
  );
}
